import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import {
  Observable,
  catchError,
  finalize,
  from,
  map,
  retry,
  switchMap,
  timer,
  throwError,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from './auth.service';
import { RestResponse } from './app.model';
import { KeycloakService } from 'keycloak-angular';
import { trimObjectValues } from './Utils';
import { AppService } from './app.service';

const AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
  private tokenRequest: Promise<string | null> | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly toast: ToastrService,
    private readonly keycloak: KeycloakService,
    private readonly appService: AppService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const pathWithoutDomain = request.url.split('/').slice(3).join('/');
    const skipAuth = request.headers.has('skip');
    const isApiRequest = request.url.startsWith(this.authService.apiURL);
    const preparedRequest = request.clone({
      headers: request.headers.delete('skip'),
      body: trimObjectValues(request.body),
    });

    this.appService.setLoading({ route: pathWithoutDomain, loading: true });

    const response$ =
      !skipAuth && isApiRequest
        ? from(this.getAccessToken(false)).pipe(
            switchMap((token) =>
              next.handle(this.withAuthorization(preparedRequest, token))
            )
          )
        : next.handle(preparedRequest);

    return response$.pipe(
      retry({
        count: 2,
        delay: (error: HttpErrorResponse, retryCount) =>
          this.isRetryableRead(preparedRequest, isApiRequest, error)
            ? timer(400 * retryCount)
            : throwError(() => error),
      }),
      map((event) => this.unwrapResponse(event)),
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 401 &&
          !skipAuth &&
          isApiRequest &&
          !preparedRequest.context.get(AUTH_RETRIED)
        ) {
          return this.retryWithFreshToken(preparedRequest, next, error);
        }

        this.showHttpError(error);
        return throwError(() => error);
      }),
      finalize(() =>
        this.appService.setLoading({ route: pathWithoutDomain, loading: false })
      )
    );
  }

  private retryWithFreshToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    return from(this.getAccessToken(true)).pipe(
      catchError(() => {
        this.toast.warning(
          'Your session expired. Please sign in again, then retry.'
        );
        return throwError(() => originalError);
      }),
      switchMap((token) => {
        if (!token) {
          this.toast.warning(
            'Your session expired. Please sign in again, then retry.'
          );
          return throwError(() => originalError);
        }

        return next
          .handle(
            this.withAuthorization(
              request.clone({
                context: request.context.set(AUTH_RETRIED, true),
              }),
              token
            )
          )
          .pipe(
            retry({
              count: 2,
              delay: (error: HttpErrorResponse, retryCount) =>
                this.isRetryableRead(request, true, error)
                  ? timer(400 * retryCount)
                  : throwError(() => error),
            }),
            map((event) => this.unwrapResponse(event)),
            catchError((error: HttpErrorResponse) => {
              this.showHttpError(error);
              return throwError(() => error);
            })
          );
      })
    );
  }

  private getAccessToken(forceRefresh: boolean): Promise<string | null> {
    if (this.tokenRequest) {
      return this.tokenRequest;
    }

    this.tokenRequest = this.keycloak
      .updateToken(forceRefresh ? -1 : 30)
      .then(() => this.keycloak.getToken())
      .then((token) => token || null)
      .catch(() => null)
      .finally(() => {
        this.tokenRequest = null;
      });

    return this.tokenRequest;
  }

  private withAuthorization(
    request: HttpRequest<unknown>,
    token: string | null
  ): HttpRequest<unknown> {
    return token
      ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : request;
  }

  private isRetryableRead(
    request: HttpRequest<unknown>,
    isApiRequest: boolean,
    error: HttpErrorResponse
  ): boolean {
    if (!isApiRequest) {
      return false;
    }

    const method = request.method.toUpperCase();
    const path = request.url.split('?')[0].replace(/\/+$/, '');
    const isSafeRead =
      method === 'GET' ||
      method === 'HEAD' ||
      (method === 'POST' && path.endsWith('/filter'));
    const status = Number(error.status ?? 0);
    const isTransient =
      status === 0 ||
      status === 408 ||
      status === 425 ||
      status === 429 ||
      status >= 500;

    return isSafeRead && isTransient;
  }

  private unwrapResponse(event: HttpEvent<unknown>): HttpEvent<unknown> {
    if (!(event instanceof HttpResponse)) {
      return event;
    }

    const response = event.body as RestResponse | null;
    if (
      response?.statusCode != null &&
      response.body !== undefined &&
      [200, 201].includes(response.statusCode)
    ) {
      return event.clone({ body: response.body });
    }

    return event;
  }

  private showHttpError(error: HttpErrorResponse): void {
    if (![400, 403, 409, 500].includes(error.status)) {
      return;
    }

    let message = error.error?.message || 'The request could not be completed.';
    const errors = error.error?.Errors as string[] | undefined;
    if (errors?.length) {
      message = errors.join(' & ');
    }
    this.toast.error(message);
  }
}
